import React from 'react';
import { StyleSheet } from 'react-native';
import truncate from 'lodash/truncate';
import { DocumentAccessLevel } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNAButton, DNASpinner, DNAText, Iffy } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNACustomFieldChipList, DNAFileTypeChip, DNABreadcrumb } from 'src/components/DNA';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';
import { useAppSettings } from 'src/state/context/AppSettings';
import { usePresentationSelector } from './PresentationSelectorStateProvider';
import { usePresentationBuilderState } from '../state/PresentationBuilderStateProvider';
import InputComponent from 'src/components/Publishers/InputComponent';

const MAX_DOCUMENT_TITLE_LENGTH = 150;
const MAX_DOCUMENT_TITLE_LENGTH_TABLET = 65;

export const S = StyleSheet.create({
  subHeaderContainer: {
    marginHorizontal: 64,
    marginVertical: 16,
  },
  breadcrumbContainer: {
    marginTop: 32,
    marginBottom: 16,
  },
  deckSearchInputContainer: {
    minWidth: 280,
    marginRight: 16,
  },
  deckSearchInputStyle: {
    borderRadius: 16,
  },
  deckSearchInputTextStyle: {
    borderRadius: 0,
  },
  tooltipText: {
    color: colors['color-text-white'],
  },
})

const SubHeader: React.FC = () => {
  const {
    selectedDocumentVersionORM,
    searchResultsText,
    isLoadingSearch,
    localSearchText,
    setSelectedDocumentVersionORM,
    isCurrentlyNested,
    folderStack,
    popToFolder,
    deckSearchText,
    setDeckSearchText,
  } = usePresentationSelector()

  const {
    selectorThumbnailSize,
    cycleSelectorThumbnailSize,
  } = usePresentationBuilderState()

  const { deviceMode } = useAppSettings()
  const isTablet = deviceMode === 'tablet'
  const truncatedDocumentTitle = truncate(selectedDocumentVersionORM?.model.title || '', {
    length: isTablet ? MAX_DOCUMENT_TITLE_LENGTH_TABLET : MAX_DOCUMENT_TITLE_LENGTH,
  })
  const isPersonalFile =
    selectedDocumentVersionORM?.relations.documentORM.model.accessLevel === DocumentAccessLevel.USER;

  const isSubFolder = isCurrentlyNested && !!folderStack.length

  return (
    <DNABox alignY="center" style={S.subHeaderContainer}>
      {/* Folder Breadcrumb Nav */}
      <Iffy is={!selectedDocumentVersionORM && isSubFolder}>
        <DNABox fill alignY="center" childFill={1} style={S.breadcrumbContainer}>
          <DNABreadcrumb
            rootTitle="My Folders"
            folderStack={folderStack}
            onRootPress={() => popToFolder()}
            onFolderPress={(folderORM) => popToFolder(folderORM)}
          />
        </DNABox>
      </Iffy>
      {/* Search Results / Item Length */}
      <Iffy is={!selectedDocumentVersionORM && !isSubFolder}>
        <DNABox style={S.breadcrumbContainer}>
          {isLoadingSearch && localSearchText ? (
            <DNABox appearance="row" alignY="center" spacing="sm">
              <DNASpinner size="small" />
              <DNAText h5>Loading results...</DNAText>
            </DNABox>
          ) : (
            <DNAText h5 status="secondary">
              {searchResultsText}
            </DNAText>
          )}
        </DNABox>
      </Iffy>
      {/* Selected Document Name & Back Button */}
      <Iffy is={selectedDocumentVersionORM}>
        <DNABox
          appearance="row"
          wrap="start"
          alignY="center"
          spacing="between"
          fill
        >
          {/* Left Section: Back Button + Title + Chips */}
          <DNABox
            alignY="center"
            appearance="row"
            shrink
          >
            <DNABox testID="custom-deck-breadcrumbs-back-button" style={{ marginRight: 16 }}>
              <DNAButton
                appearance="outline"
                status="tertiary"
                iconLeft="caret-left"
                onPress={() => setSelectedDocumentVersionORM(undefined)}
              />
            </DNABox>
            <DNABox appearance="col" fill spacing="sm">
              <DNAPopover disablePopover={!selectedDocumentVersionORM?.model.title}>
                <DNAPopover.Anchor>
                  <DNAText numberOfLines={1} b1>
                    {truncatedDocumentTitle}
                  </DNAText>
                </DNAPopover.Anchor>
                <DNAPopover.Content>
                  <DNAText style={S.tooltipText}>
                    {selectedDocumentVersionORM?.model.title || ''}
                  </DNAText>
                </DNAPopover.Content>
              </DNAPopover>
              <DNABox appearance="row" spacing="md">
                <Iffy is={!isPersonalFile}>
                  <DNACustomFieldChipList documentVersionORM={selectedDocumentVersionORM}/>
                </Iffy>
                { selectedDocumentVersionORM &&
                  <DNAFileTypeChip orm={selectedDocumentVersionORM} />
                }
              </DNABox>
            </DNABox>
          </DNABox>

          {/* Right Section Wrapper: Container for View Toggle & Search */}
          <DNABox
            fill
            appearance="row"
            alignY="center"
            spacing="sm"
            style={{
              marginTop: 12,
            }}
          >
            { !isTablet && <DNABox>
              <DNAButton
                appearance="outline"
                status="tertiary"
                iconLeft={selectorThumbnailSize === 'lg' ? 'grid-nine' : 'squares-four'}
                onPress={cycleSelectorThumbnailSize}
              />
            </DNABox> }

            <DNABox fill style={S.deckSearchInputContainer}>
              <InputComponent
                value={deckSearchText}
                onChangeText={setDeckSearchText}
                required={false}
                size={isTablet ? 'md' : 'sm'}
                placeHolder="Search slide titles and keywords"
                hideLabel
                removeMarginPadding
                inputStyle={S.deckSearchInputStyle}
                textStyle={S.deckSearchInputTextStyle}
                testID="deck-slide-search"
                getLeftIconFunction={() => (
                  <DNAButton
                    appearance="ghost"
                    status="tertiary"
                    iconLeft="magnifying-glass"
                    disabled
                  />
                )}
                getRightIconFunction={() => (
                  <Iffy is={deckSearchText}>
                    <DNAButton
                      appearance="ghost"
                      status="tertiary"
                      iconLeft="x"
                      onPress={() => setDeckSearchText('')}
                    />
                  </Iffy>
                )}
              />
            </DNABox>
          </DNABox>
        </DNABox>
      </Iffy>
    </DNABox>

  )
}

export default SubHeader
